import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, FlaskConical, Send } from "lucide-react";
import { useState } from "react";

import {
  sendBatches,
  type Progress as Prog,
  type SendResponse,
  type Vendor,
} from "./sendClient";

/**
 * The one place in the CRM that can spend money.
 *
 * WHAT THIS DIALOG IS FOR
 * -----------------------
 * Not to make sending convenient. Sending is already one HTTP call; a dialog
 * adds nothing to that. It exists to make the IRREVERSIBLE step deliberate, and
 * to keep the two modes visibly different things rather than a checkbox apart.
 *
 *   Free test    Stannp renders a proof PDF, dispatches nothing, charges
 *                nothing, and writes nothing to the ledger. Rehearse here.
 *   Send for real  Buys postage. Cannot be undone by any button.
 *
 * THE FRICTION IS PROPORTIONAL, ON PURPOSE
 * ----------------------------------------
 * One card asks for a tick. A whole cell asks you to type its code. Uniform
 * friction is what trains people to click through it -- if a single test card
 * demanded the same ceremony as 285 real ones, the ceremony would stop meaning
 * anything by the time it mattered.
 *
 * WHY IT REPORTS `recorded` SEPARATELY FROM `accepted`
 * ---------------------------------------------------
 * They should be equal. When they are not, the printer took cards that the
 * ledger does not know about -- money spent with no record of which doors got
 * it. That is the single most expensive failure available here, so it gets its
 * own line and its own color instead of being folded into a success message.
 */

type Props = {
  /** Either a whole drop... */
  cell?: string;
  touch?: string;
  /** ...or an explicit set of mailings (a single card, typically). */
  mailingIds?: number[];
  /** How many cards this would send. Drives the friction level. */
  count: number;
  /** Printer chosen upstream (from the schedule row), if any. */
  vendor?: Vendor | null;
  /** Set when sending is impossible; the trigger is disabled and says why. */
  blocked?: string | null;
  label?: string;
  onDone?: () => void;
};

/** Above this, a live send asks you to type the cell code rather than tick a box. */
const TYPE_TO_CONFIRM_ABOVE = 10;

export function SendDialog({
  cell,
  touch,
  mailingIds,
  count,
  vendor,
  blocked,
  /**
   * "Send or test", not "Send".
   *
   * The free rehearsal lives inside this dialog, and the only way in was a
   * button marked Send. That is precisely the button someone being careful will
   * not press to find out what is behind it -- so the safe option was hidden
   * behind the scary one, and the person most likely to want a rehearsal was
   * the least likely to find it.
   */
  label = "Send or test",
  onDone,
}: Props) {
  const [open, setOpen] = useState(false);
  // PCM when the drop has not chosen, because the fallback is not neutral: it
  // decides which button the dialog even offers. Defaulting to Stannp meant a
  // drop with "printer not decided" silently showed "Send a free test" -- a
  // Stannp rehearsal needing a $12/month plan we do not hold -- and hid the
  // PCM sandbox order, which is free, needs no subscription, and is the one
  // that can actually be run today.
  //
  // This picks what the DIALOG shows, not what gets stored. plan_drop still
  // refuses to guess a vendor, because writing one is a different act.
  const [printer, setPrinter] = useState<Vendor>(vendor ?? "pcm");
  const [understood, setUnderstood] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState<null | "test" | "live">(null);
  const [prog, setProg] = useState<Prog | null>(null);
  const [result, setResult] = useState<SendResponse | null>(null);

  const needsTyping = count > TYPE_TO_CONFIRM_ABOVE;
  const phrase = cell ?? "SEND";
  const liveReady =
    understood &&
    (!needsTyping || typed.trim().toUpperCase() === phrase.toUpperCase());

  const reset = () => {
    setUnderstood(false);
    setTyped("");
    setProg(null);
    setResult(null);
  };

  const go = async (mode: "test" | "live") => {
    setBusy(mode);
    setResult(null);
    setProg(null);
    const { last, totals } = await sendBatches(
      {
        vendor: printer,
        mode,
        confirm: mode === "live",
        ...(mailingIds?.length ? { mailing_ids: mailingIds } : { cell, touch }),
      },
      (p) => setProg(p),
    );
    setProg(totals);
    setResult(last);
    setBusy(null);
    // Refresh the caller even on a partial failure: some cards may have gone,
    // and a stale screen showing them as still planned is its own hazard.
    onDone?.();
  };

  const pct =
    prog && prog.accepted + prog.remaining > 0
      ? Math.round((prog.accepted / (prog.accepted + prog.remaining)) * 100)
      : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          size="sm"
          disabled={!!blocked || count === 0}
          title={blocked ?? undefined}
        >
          <Send className="size-4" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {count} card{count === 1 ? "" : "s"}
            {cell && touch ? ` · ${cell} ${touch}` : ""}
          </DialogTitle>
          <DialogDescription>
            Two different things below. The first costs nothing and posts
            nothing; the second buys postage and cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Printer</div>
              <Select
                value={printer}
                onValueChange={(v) => setPrinter(v as Vendor)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stannp">Stannp</SelectItem>
                  <SelectItem value="pcm">PCM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {printer === "pcm" && (
              <p className="text-xs text-muted-foreground flex-1">
                PCM cannot print these PDFs yet — its order endpoint takes HTML
                and how it accepts a PDF is unconfirmed. It will refuse rather
                than guess. Worth resolving: PCM has no access fee and is
                cheaper per card above 501/month.
              </p>
            )}
          </div>

          {/* ── the free path ── */}
          <div className="rounded-md border p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <FlaskConical className="size-4" />{" "}
              {printer === "pcm" ? "Sandbox order — free" : "Free rehearsal"}
            </div>
            <p className="text-xs text-muted-foreground">
              {printer === "pcm"
                ? "Placed with PCM's sandbox credential. They accept and validate the whole order — artwork, addresses, mail class — and then do not process it: nothing is printed, posted or charged."
                : "Stannp renders the real artwork and returns a proof PDF. Nothing is posted and nothing is charged for the call, though reaching their API at all needs a $12/month plan."}{" "}
              The ledger is untouched either way, so the cards stay planned and
              can still be sent for real afterwards.
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={busy !== null}
              onClick={() => go("test")}
            >
              {busy === "test"
                ? "Testing…"
                : printer === "pcm"
                  ? "Place a sandbox order"
                  : "Send a free test"}
            </Button>
          </div>

          {/* ── the paid path ── */}
          <div className="rounded-md border border-destructive/40 p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-destructive">
              <AlertTriangle className="size-4" /> This buys postage
            </div>
            <label className="flex items-start gap-2 text-xs">
              <Checkbox
                checked={understood}
                onCheckedChange={(v) => setUnderstood(v === true)}
                className="mt-0.5"
              />
              <span>
                I understand this posts {count} real card
                {count === 1 ? "" : "s"} and cannot be undone.
              </span>
            </label>
            {needsTyping && (
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">
                  Type <b>{phrase}</b> to confirm a drop this size.
                </div>
                <Input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  className="w-40 font-mono"
                  placeholder={phrase}
                />
              </div>
            )}
            <Button
              variant="destructive"
              size="sm"
              disabled={busy !== null || !liveReady}
              onClick={() => go("live")}
            >
              {busy === "live"
                ? "Sending…"
                : `Post ${count} card${count === 1 ? "" : "s"}`}
            </Button>
          </div>

          {busy && prog && (
            <div className="space-y-1">
              <Progress value={pct} />
              <p className="text-xs text-muted-foreground tabular-nums">
                {prog.accepted} accepted · {prog.remaining} left · batch{" "}
                {prog.batches}
              </p>
            </div>
          )}

          {result && <Outcome result={result} totals={prog} />}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={busy !== null}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The result, stated plainly -- including the parts that are bad news. */
function Outcome({
  result,
  totals,
}: {
  result: SendResponse;
  totals: Prog | null;
}) {
  if (result.error) {
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm space-y-1">
        <div className="font-medium text-destructive">{result.error}</div>
        {result.detail && <p className="text-xs">{result.detail}</p>}
        {result.fix && <p className="text-xs font-mono">{result.fix}</p>}
        {result.problems?.length ? (
          <ul className="text-xs list-disc pl-4">
            {result.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  const accepted = totals?.accepted ?? result.accepted ?? 0;
  const recorded = totals?.recorded ?? result.recorded ?? 0;
  const test = result.mode === "test";
  // The discrepancy that costs money: cards printed, no record of which doors.
  const mismatch = !test && accepted !== recorded;

  return (
    <div className="rounded-md border p-3 text-sm space-y-2">
      <div className="flex flex-wrap gap-2">
        <Badge variant={test ? "outline" : "secondary"}>
          {test ? "test — nothing posted" : "posted"}
        </Badge>
        <Badge variant="outline">{accepted} accepted</Badge>
        {!test && (
          <Badge variant={mismatch ? "destructive" : "outline"}>
            {recorded} recorded
          </Badge>
        )}
        {result.remaining ? (
          <Badge variant="outline">{result.remaining} still planned</Badge>
        ) : null}
      </div>

      {mismatch && (
        <p className="text-xs text-destructive">
          The printer accepted {accepted} but the ledger recorded {recorded}.
          Postage was spent on cards we cannot attribute to a door. Do not
          re-send — reconcile first.
        </p>
      )}
      {result.record_error && (
        <p className="text-xs text-destructive">
          Write-back failed: {result.record_error}
        </p>
      )}
      {test && result.proofs?.length ? (
        <div className="text-xs space-y-1">
          <div className="text-muted-foreground">
            Stannp's own rendering — better evidence than any check of ours:
          </div>
          {result.proofs.map((u, i) => (
            <a
              key={u}
              href={u}
              target="_blank"
              rel="noreferrer"
              className="underline block"
            >
              proof {i + 1}
            </a>
          ))}
        </div>
      ) : null}
      {result.failures?.length ? (
        <details className="text-xs">
          <summary className="cursor-pointer">
            {result.failures.length} piece
            {result.failures.length === 1 ? "" : "s"} refused
          </summary>
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            {result.failures.slice(0, 20).map((f) => (
              <li key={f.ref}>
                <span className="font-mono">{f.ref}</span>: {f.error}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {result.note && (
        <p className="text-xs text-muted-foreground">{result.note}</p>
      )}
    </div>
  );
}
